import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, FlaskConical } from "lucide-react";
import { SyncBar } from "./SyncBar";
import { NetworkReport } from "./NetworkReport";
import { ParentsDialog } from "./ParentsDialog";
import api from "@/lib/api";
import { MonitoringMap } from "./MonitoringMap";
import { PlantDetail } from "./PlantDetail";
import { LIGHT, nf, kw, ago, DIREZ } from "./shared";
import { BulkAssign } from "./BulkAssign";
import { QualitaDati } from "./QualitaDati";

const Kpi = ({ label, value, testid, color }) => (
  <div className="rounded-xl border border-neutral-800 bg-neutral-950 p-3">
    <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">{label}</div>
    <div className="mt-1 font-mono text-xl font-black" style={{ color }} data-testid={testid}>{value}</div>
  </div>
);

const Filters = ({ k, f, setF, showPartner }) => (
  <div className="flex flex-wrap gap-2" data-testid="monitoring-filters">
    {[["all", "Tutti", "#fff", k.totale], ...[1, 3, 2, 4].map((l) => [String(l), LIGHT[l].label, LIGHT[l].color, k[`light_${l}`]]), ...(showPartner ? [["abbinare", "Da abbinare", "#fbbf24", k.da_abbinare]] : []), ...(k.direzionali ? [["direz", "Direzionali", "#7dd3fc", k.direzionali]] : [])].map(([key, label, c, n]) => (
      <button key={key} type="button" onClick={() => setF(key)} data-testid={`monitoring-filter-${key}`}
        className="flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold transition-colors"
        style={f === key ? { borderColor: c, color: c, background: `${c}18` } : { borderColor: "#262626", color: "#a3a3a3" }}>
        <span className="h-2 w-2 rounded-full" style={{ background: c }} />{label} ({n})
      </button>
    ))}
  </div>
);

const Row = ({ p, onClick, showPartner, sel, onSel }) => (
  <div className="flex items-center gap-2">
  {onSel && <input type="checkbox" checked={sel} onChange={onSel} disabled={p.mock} className="h-4 w-4 shrink-0 accent-sky-400" data-testid={`monitoring-select-${p.id}`} />}
  <button type="button" onClick={onClick} data-testid={`monitoring-plant-${p.id}`}
    className="flex w-full items-center gap-3 rounded-xl border border-neutral-800 bg-neutral-950 px-3 py-2.5 text-left transition-colors hover:border-neutral-500">
    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: LIGHT[p.light].color, boxShadow: `0 0 8px ${LIGHT[p.light].color}` }} />
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm font-semibold text-white">{p.nome} {p.comune && <span className="font-normal text-neutral-500">· {p.comune}</span>}
        {showPartner && !p.partner_id && <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-300" data-testid={`plant-unassigned-${p.id}`}>Da abbinare</span>}
        {p.partner_id === DIREZ && <span className="ml-2 rounded-full bg-sky-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-sky-300" data-testid={`plant-direz-${p.id}`}>Direzionale</span>}</div>
      <div className="truncate text-xs text-neutral-400">{[`${nf(p.kwp, 2)} kWp`, showPartner && p.partner_name, p.allarmi_attivi ? `${p.allarmi_attivi} allarme attivo` : null, `agg. ${ago(p.last_update)}`].filter(Boolean).join(" · ")}</div>
    </div>
    <div className="shrink-0 text-right">
      <div className="font-mono text-sm font-bold text-white">{kw(p.pac_w)}</div>
      <div className="text-[11px] text-neutral-500">{nf(p.e_oggi_kwh)} kWh oggi{p.soc != null ? ` · SOC ${p.soc}%` : ""}</div>
    </div>
  </button>
  </div>
);

export function TorriMonitoring({ base, params = {}, accent, showPartner = false, canSync = false, aiReport = false, focus, stato, onFocusClear }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState("all");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [parents, setParents] = useState(false);
  const [pick, setPick] = useState([]);
  const toggle = (id) => setPick((x) => (x.includes(id) ? x.filter((i) => i !== id) : [...x, id]));
  useEffect(() => { if (focus) setSel(focus); }, [focus]);
  useEffect(() => { if (stato) { setF(stato); setSel(null); } }, [stato]);
  const load = () => api.get(`${base}/plants`, { params }).then((r) => setD(r.data)).catch((e) => setD({ plants: [], kpi: {}, error: e.response?.data?.detail || "Errore di caricamento" }));
  useEffect(() => { load(); }, [base]); // eslint-disable-line react-hooks/exhaustive-deps
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (d?.plants || []).filter((p) => (f === "all" || (f === "abbinare" ? !p.partner_id : f === "direz" ? p.partner_id === DIREZ : String(p.light) === f)) && (!s || [p.nome, p.comune, p.partner_name, p.sn, p.account, p.parent_id].join(" ").toLowerCase().includes(s)));
  }, [d, f, q]);
  if (!d) return <Loader2 className="h-6 w-6 animate-spin" style={{ color: accent }} />;
  if (d.error) return <p className="text-sm text-red-400" data-testid="monitoring-error">{d.error}</p>;
  const k = d.kpi;
  return (
    <div className="space-y-5" data-testid="torri-monitoring">
      {d.mock && <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300" data-testid="monitoring-mock-banner"><FlaskConical className="h-4 w-4" />Dati dimostrativi: il collegamento al cloud FIER AURA è in arrivo.</div>}
      {aiReport && !d.mock && <NetworkReport base={base} params={params} accent={accent} />}
      {canSync && <SyncBar initial={d.sync} reload={load} openParents={() => setParents(true)} />}
      {canSync && base === "/monitoring" && <QualitaDati onOpenPlant={setSel} />}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi label="Impianti" value={k.totale} testid="kpi-totale" color="#fff" />
        <Kpi label="Potenza ora" value={`${nf(k.potenza_kw)} kW`} testid="kpi-potenza" color={accent} />
        <Kpi label="Prodotti oggi" value={`${nf(k.oggi_kwh)} kWh`} testid="kpi-oggi" color="#fff" />
        <Kpi label="kWp installati" value={nf(k.kwp_totali)} testid="kpi-kwp" color="#fff" />
        <Kpi label="Allarmi attivi" value={k.allarmi_attivi} testid="kpi-allarmi" color={k.allarmi_attivi ? "#ef4444" : "#22c55e"} />
        <Kpi label="Offline >24h" value={k.light_4} testid="kpi-offline" color={k.light_4 ? "#a3a3a3" : "#22c55e"} />
      </div>
      {!k.totale ? <p className="text-sm text-neutral-500" data-testid="monitoring-empty">Nessun impianto collegato al monitoraggio.</p> : (
        <>
          <MonitoringMap plants={list} onSelect={setSel} />
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Filters k={k} f={f} setF={setF} showPartner={showPartner} />
            <label className="flex items-center gap-2 rounded-full border border-neutral-800 px-3 py-1.5 text-sm">
              <Search className="h-4 w-4 text-neutral-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca impianto, comune, SN…" className="w-48 bg-transparent text-white outline-none placeholder:text-neutral-600" data-testid="monitoring-search" />
            </label>
          </div>
          {canSync && !!pick.length && <BulkAssign ids={pick} onDone={() => { setPick([]); load(); }} onClear={() => setPick([])} onAll={() => setPick(list.filter((p) => !p.mock).map((p) => p.id))} />}
          <div className="space-y-2" data-testid="monitoring-list">
            {list.map((p) => <Row key={p.id} p={p} onClick={() => setSel(p.id)} showPartner={showPartner} sel={pick.includes(p.id)} onSel={canSync ? () => toggle(p.id) : null} />)}
            {!list.length && <p className="text-sm text-neutral-500" data-testid="monitoring-list-empty">Nessun impianto con questi filtri.</p>}
          </div>
        </>
      )}
      <PlantDetail base={base} params={params} plantId={sel} onClose={() => { setSel(null); onFocusClear?.(); }} accent={accent} canAssign={canSync} onChanged={load} />
      {canSync && <ParentsDialog open={parents} onOpenChange={setParents} onChanged={load} />}
    </div>
  );
}
